import React, { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  useCarousel,
  useTop10,
  useQuickLists,
  useNewReleases,
  useContinueWatching,
  useTvSeries,
  useComingSoon,
} from '../hooks/useHome';
import { movieApi } from '../api/client';
import { useAuthStore } from '../store';
import { toTitle, toTitles, toCategory, slugify } from '../lib/catalogue';

import { HeroCarousel } from '../components/hero-carousel';
import { TopTenRow } from '../components/top-ten-row';
import { TitleRow } from '../components/title-row';
import { CategoryGrid } from '../components/category-grid';
import { SubscribeBand } from '../components/subscribe-band';

export default function HomePage() {
  usePageTitle(
    'Kast — Stream movies and series',
    'Watch films and series on Kast. New releases, weekly top ten, and African stories — streaming on any screen.'
  );
  const { token } = useAuthStore();

  const { data: rawCarousel = [], isLoading: carouselLoading } = useCarousel(10);
  const { data: rawTop10 = [] } = useTop10();
  const { data: rawQuickLists = [] } = useQuickLists();
  const { data: rawNewReleases = [] } = useNewReleases(12);
  const { data: rawContinueWatching = [] } = useContinueWatching(!!token);
  const { data: tvSeriesResult } = useTvSeries(1, 16);
  const { data: rawComingSoon = [] } = useComingSoon(10);

  const { data: rawCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await movieApi.getCategories();
      if (!data.success) return [];
      return data.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const carouselTitles = useMemo(() => toTitles(rawCarousel), [rawCarousel]);
  const newReleasesTitles = useMemo(() => toTitles(rawNewReleases), [rawNewReleases]);

  // The hero is a random pick (see useCarousel), so pin the first set that arrives for the whole
  // visit. Otherwise any later refetch of it — e.g. a broad query invalidation after tapping My
  // List — would hand back different titles and swap the slides under the viewer.
  const heroTitlesRef = useRef(null);
  if (heroTitlesRef.current === null && carouselTitles.length > 0) {
    heroTitlesRef.current = carouselTitles;
  }
  const featured = heroTitlesRef.current ?? newReleasesTitles;

  const continueWatchingTitles = useMemo(() => {
    return rawContinueWatching.map((cw) =>
      toTitle({
        Id: cw.MovieId,
        Title: cw.MovieTitle,
        ThumbnailUrl: cw.ThumbnailUrl,
        EpisodeCount: 1,
        _resumeEpisodeId: cw.EpisodeId,
        _progressPercent: cw.ProgressPercent,
      })
    ).filter(Boolean);
  }, [rawContinueWatching]);

  const top10Titles = useMemo(() => toTitles(rawTop10), [rawTop10]);
  const tvSeriesTitles = useMemo(() => toTitles(tvSeriesResult?.data ?? []), [tvSeriesResult]);
  const comingSoonTitles = useMemo(() => toTitles(rawComingSoon), [rawComingSoon]);

  const quickLists = useMemo(() => {
    return rawQuickLists
      .map((q) => ({
        id: q.CategoryId,
        name: q.CategoryName ?? '',
        slug: slugify(q.CategoryName ?? ''),
        titles: toTitles(q.Movies ?? []),
      }))
      .filter((q) => q.titles.length > 0);
  }, [rawQuickLists]);

  const categories = useMemo(() => {
    return (rawCategories || [])
      .map(toCategory)
      .filter((c) => c && c.count > 0);
  }, [rawCategories]);

  return (
    <div className="flex flex-col min-h-screen">
      {carouselLoading ? (
        <div className="w-full aspect-[21/9] min-h-[420px] max-h-[720px] bg-card/20 animate-pulse" />
      ) : featured.length > 0 ? (
        <HeroCarousel titles={featured.slice(0, 10)} />
      ) : (
        <EmptyCatalogue />
      )}

      <div className="relative z-10 mt-1 pb-8">
        {continueWatchingTitles.length > 0 && (
          <TitleRow
            heading="Continue Watching"
            titles={continueWatchingTitles}
            variant="landscape"
            priority
          />
        )}

        <TopTenRow titles={top10Titles} />

        <TitleRow
          heading="New on Kast"
          href="/browse?sort=new"
          titles={newReleasesTitles}
          priority
        />

        {tvSeriesTitles.length > 0 && (
          <TitleRow
            heading="Series to Binge"
            href="/browse?type=Series"
            titles={tvSeriesTitles}
          />
        )}

        <TitleRow
          heading="Coming Soon"
          note="Add them now, watch on release day"
          titles={comingSoonTitles}
        />

        {quickLists.map((list) => (
          <TitleRow
            key={list.id}
            heading={list.name}
            href={`/categories`}
            titles={list.titles}
          />
        ))}

        {categories.length > 0 && <CategoryGrid categories={categories} />}
      </div>

      <SubscribeBand />
    </div>
  );
}

function EmptyCatalogue() {
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-[1600px] flex-col justify-center gutter py-16">
      <h1 className="display max-w-[18ch] text-[clamp(2rem,5vw,3.5rem)] text-balance text-foreground font-bold">
        Nothing to watch here yet
      </h1>
      <p className="mt-4 max-w-[50ch] text-base text-muted-foreground">
        The catalogue could not be loaded. Refresh in a moment, or browse the full
        genre list to see what has been published.
      </p>
    </div>
  );
}
